import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";

// =============================================================================
// Finding a post among hundreds.
//
// Written because the calendar was quietly showing only the current month,
// with nothing on screen saying so — posts scheduled for December simply did
// not exist as far as the page was concerned. The range is now a control
// rather than a hidden default, which is half the fix; the other half is that
// "everything" is one of the options.
//
// Presets first, custom dates behind them. Nobody opening a calendar wants to
// type two ISO dates to see next week, but somebody chasing one post from
// March does want to be able to.
// =============================================================================

export type RangeKey = "week" | "month" | "next30" | "past30" | "upcoming" | "all" | "custom";

export type CalendarRange = { key: RangeKey; from?: string; to?: string };

const DAY = 86_400_000;
const iso = (d: Date) => d.toISOString();

/**
 * Turns a preset into the bounds the API takes.
 *
 * Open-ended on purpose where it makes sense: "upcoming" has no end, and
 * "all" has neither bound. The API treats a missing bound as unbounded on
 * that side, so neither needs a sentinel date invented here.
 */
export function boundsFor(range: CalendarRange): { from?: string; to?: string } {
  const now = new Date();
  const startOfToday = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );

  switch (range.key) {
    case "week":
      return { from: iso(startOfToday), to: iso(new Date(startOfToday.getTime() + 7 * DAY)) };
    case "month":
      return {
        from: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))),
        to: iso(
          new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59)),
        ),
      };
    case "next30":
      return { from: iso(startOfToday), to: iso(new Date(startOfToday.getTime() + 30 * DAY)) };
    case "past30":
      return { from: iso(new Date(startOfToday.getTime() - 30 * DAY)), to: iso(now) };
    case "upcoming":
      return { from: iso(now) };
    case "all":
      return {};
    case "custom":
      return {
        // A date input gives a plain date; the API wants an instant. The end
        // is pushed to the last moment of its day, or a post scheduled that
        // afternoon falls outside a range that names its own date.
        from: range.from ? iso(new Date(`${range.from}T00:00:00.000Z`)) : undefined,
        to: range.to ? iso(new Date(`${range.to}T23:59:59.999Z`)) : undefined,
      };
  }
}

const PRESETS: RangeKey[] = ["week", "month", "next30", "past30", "upcoming", "all"];

export function CalendarFilter({
  range,
  onRange,
  query,
  onQuery,
  counts,
}: {
  range: CalendarRange;
  onRange: (r: CalendarRange) => void;
  query: string;
  onQuery: (q: string) => void;
  /** Shown beside the search so the filter's effect is visible, not guessed. */
  counts: { showing: number; total: number };
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-3 rounded-xl border border-border bg-white p-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {PRESETS.map((key) => (
          <button
            key={key}
            onClick={() => onRange({ key })}
            className={`rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
              range.key === key
                ? "bg-night-900 text-white"
                : "border border-border text-muted hover:text-ink"
            }`}
          >
            {t(`calendar.range.${key}`)}
          </button>
        ))}
        <button
          onClick={() => onRange({ key: "custom", from: range.from, to: range.to })}
          className={`rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
            range.key === "custom"
              ? "bg-night-900 text-white"
              : "border border-border text-muted hover:text-ink"
          }`}
        >
          {t("calendar.range.custom")}
        </button>
      </div>

      {range.key === "custom" ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <input
            type="date"
            value={range.from ?? ""}
            onChange={(e) => onRange({ ...range, key: "custom", from: e.target.value })}
            className="input w-auto"
            aria-label={t("calendar.range.fromLabel")}
          />
          <span className="text-muted">{t("calendar.range.toWord")}</span>
          <input
            type="date"
            value={range.to ?? ""}
            onChange={(e) => onRange({ ...range, key: "custom", to: e.target.value })}
            className="input w-auto"
            aria-label={t("calendar.range.toLabel")}
          />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder={t("calendar.searchPlaceholder")}
            className="input pl-9"
          />
        </div>
        <span className="text-xs tabular-nums text-muted">
          {counts.showing === counts.total
            ? t("calendar.showingAll", { count: counts.total })
            : t("calendar.showingSome", { showing: counts.showing, total: counts.total })}
        </span>
      </div>
    </div>
  );
}
